/**
 * Types for the complexity-adjustment model (internally "FlowBot").
 *
 * FlowBot replaces random phrase ordering in the Sight Reading Trainer with a
 * per-phrase recurrence that slides a difficulty "band" up when the user is
 * cruising and down when they are struggling, keeping them in a productive flow
 * zone. Source of truth for the maths is `ComplexityOracle.xlsx`; the reverse-
 * engineered semantics are documented in
 * `documentation/complexity-oracle-findings.md`.
 *
 * These types are deliberately free of Phaser and xstate imports so that the
 * pure model can be unit-tested — and validated against the oracle — in
 * isolation from the scene.
 */

/** Per-phrase performance grade.
 *
 *  Derived from the phrase's *complexity* error tally, which is maintained
 *  separately from `EventStream`'s accuracy scoring: a red note contributes
 *  1.0 and a yellow note 0.5, per the engineering spec. (`EventStream`
 *  scores semi-errors at 0.7 for accuracy purposes; that scale is NOT used
 *  here, because the oracle's thresholds were calibrated against 0.5.) */
export enum Verdict {
  /** `error_total <= green_max` — the band moves UP. */
  Green = 'G',
  /** `green_max < error_total <= yellow_max` — the band HOLDS. */
  Yellow = 'Y',
  /** `error_total > yellow_max` — the band moves DOWN. */
  Red = 'R',
}

/**
 * How the band sits around the scalar the recurrence tracks.
 * See {@link TierConfig.anchor}.
 */
export type BandAnchor = 'floor' | 'center';

/**
 * Tunables for one tier. See {@link DEFAULT_TIER_CONFIG} for the shipping
 * values and {@link ORACLE_SHEET_PRESET} for the sheet's own.
 *
 * ⚠️ `aim`/`gim` deliberately differ from the oracle sheet, by product ruling
 * of 2026-08-01. Earlier revisions of this comment said the opposite — that the
 * spec docs were stale for quoting `0.5 / 1.0`. They were not; that pair ships.
 * See findings §1, Conflict 1.
 */
export interface TierConfig {
  /** Seed position for assessment phrases 1 and 2. Oracle `C3`.
   *
   *  Under centre anchoring this is the band's MIDPOINT, so the shipped `0.20`
   *  opens a new user at `[0.10, 0.30]`. Ratified deliberately (#145): the
   *  ceiling is unchanged from the floor-anchored era and the easy side widens;
   *  raising the seed to preserve the old floor was rejected. */
  complexityStart: number;
  /** CVR — total band WIDTH. How it sits around the position depends on
   *  {@link TierConfig.anchor}. */
  cvr: number;
  /**
   * Where the band sits relative to the position the recurrence tracks.
   *
   * - `'floor'` — the position IS the band floor: `[p, p + cvr]`. This is what
   *   the oracle sheet does; its `C` column is the floor and `D` is `C + CVR`.
   * - `'center'` — the position is the band CENTRE: `[p - cvr/2, p + cvr/2]`.
   *   The v1 model, ruled 2026-08-02.
   *
   * This is not cosmetic: it moves the recurrence's own clamps. A floor may run
   * up to `1 - cvr` before the band leaves `[0, 1]`; a centre may run to
   * `1 - cvr/2`. The golden vector reaches its clamp, so getting this wrong
   * silently changes the trajectory near the cap. See {@link positionBounds}.
   */
  anchor: BandAnchor;
  /** CAI — base up-step on green. Surfaced to users as **Climb Rate**;
   *  constrained to {@link RATE_RANGE}. */
  cai: number;
  /** CDI — base down-step on red. Surfaced to users as **Drop Rate**;
   *  constrained to {@link RATE_RANGE}. */
  cdi: number;
  /** PAD — application delay, counted in *assessment* phrases only.
   *
   *  A verdict earned at assessment phrase `p` drives the floor at `p + pad`.
   *  This mirrors the render pipeline: `PhraseManager` requests the next phrase
   *  at phrase end and consumes it at measure 2 of the following phrase, so a
   *  phrase requested at the end of `p` first *plays* as `p + 2`. Web = 2,
   *  mobile = 3. */
  pad: number;
  /** AIM — arithmetic (additive) streak multiplier. `0.5` with Streak Boost ON,
   *  `0.0` with it OFF. (The oracle sheet's own `1.0` is not what ships — see
   *  {@link ORACLE_SHEET_PRESET}.) */
  aim: number;
  /** GIM — geometric (multiplicative) streak multiplier. `1.0` with Streak
   *  Boost ON, and also `1.0` with it OFF — the toggle rides on `aim` alone
   *  under the v1 pair. (The sheet's own `2.0` is not what ships.) */
  gim: number;
  /** CAT — chevron eligibility threshold. Gated on the band's POSITION
   *  (`position >= cat`), not on `crMin`; not used by the recurrence, carried
   *  here because it is per-tier config. */
  cat: number;
  /** `error_total <= greenMax` classifies as {@link Verdict.Green}. */
  greenMax: number;
  /** `error_total <= yellowMax` classifies as {@link Verdict.Yellow}, else red. */
  yellowMax: number;
  /**
   * Optional explicit ceiling for the tracked position, overriding the
   * anchor-derived default in {@link positionBounds}. Undefined (the shipping
   * case) means the anchor default: 1 centre-anchored, `1 - cvr` floor-anchored.
   * Exists so a historical capture can be replayed under the position cap that
   * was in effect when it was recorded; the shipping config never sets it.
   */
  positionCeiling?: number;

  // ---- Flow Regeneration (rest phrases) ----

  /** Assessment phrases that must elapse between injected rest phrases. */
  restCadence: number;
  /**
   * Whether rest phrases are injected at all. Optional and defaulting to true,
   * so a config that predates the user-facing toggle behaves as it always did;
   * the settings panel ships it off.
   */
  restEnabled?: boolean;
  /** How far below the current POSITION rest phrases are drawn, in absolute
   *  normalized complexity: `rest = max(position - restPhraseDepth, minimum)`,
   *  re-derived into a band by the tier's anchoring. Subtracting from the
   *  position rather than from `crMin` keeps the drop the same size under either
   *  anchor; at `cvr 0.20` the effective depth below the window floor is 0.15.
   *
   *  0.25 per the 2026-07-28 test round, ruled #143 / #144 item 3. Supersedes
   *  the 0.10 default and the interim 0.20 — playtesting found 0.20 too shallow
   *  to read as a rest; `0.10` is the documented revert target. All are
   *  user-customisable anyway. */
  restPhraseDepth: number;
  /** Live accuracy (0-100) at or above which rest phrases are never injected —
   *  the user is in flow and must not be interrupted. */
  restAccuracyThreshold: number;
  /** Live accuracy (0-100) BELOW which rest phrases are also never injected.
   *
   *  The lower bound of the rest window, and strictly below
   *  {@link TierConfig.restAccuracyThreshold} — an equal pair describes an empty
   *  window and is rejected. Below the floor the user is not stuck in the sense
   *  rest phrases address: they are failing outright, typically pinned at the
   *  lower position clamp (`cvr/2`, where the band's own floor reaches 0.0),
   *  which makes `isStuck` trivially true because the band cannot fall further.
   *  Injecting there stacks rest phrases onto someone already playing the
   *  easiest material available, which neither helps nor recovers. Rest phrases
   *  are for a user who is close, not one who is under water. */
  restAccuracyFloor: number;
  /** How many consecutive unchanged POSITIONS mean the user is STUCK.
   *
   *  ⚠️ This is NOT the oracle's `PLATEAU`. That variable is an anti-jitter
   *  cooldown meaning "the band just moved" — the opposite of stuck. Because
   *  the cooldown makes a progressing band alternate move/hold, this window
   *  must exceed 2 for a climbing user not to read as stuck; 4 ships. */
  stuckWindow: number;
}

/**
 * Source of the live accuracy percentage that gates rest phrases and chevrons.
 *
 * The spec calls this "WMA", but no weighted moving average exists in the
 * client today — `EventStream.calcAccuracy()` is a rank-weighted recency ratio
 * over the accuracy queue. This interface exists so a true WMA can be
 * substituted later without touching the complexity model.
 *
 * TODO(complexity): replace the EventStream-backed implementation with a real
 * WMA once product confirms the intended definition. See findings §7 Q6.
 */
export interface AccuracySource {
  /** Live accuracy as a percentage in [0, 100]. */
  currentAccuracy(): number;
}

/**
 * Bounds for the Climb Rate / Drop Rate controls (`cai` / `cdi`), settled
 * 2026-08-01.
 *
 * The control is a **continuous** slider between these bounds; `easy`, `medium`
 * and `hard` are labelled marks along it, not the only selectable values.
 *
 * The `0.03` floor was previously assumed unusable — the worry being that a step
 * smaller than the cooldown tolerance would be swallowed by the anti-jitter
 * cooldown and freeze the band. Findings §3 established that the cooldown is not
 * stuck-detection, and the behaviour was then confirmed directly: at
 * `cai = 0.03` a green streak climbs `0.20 → 0.23 → 0.26 → 0.305 → …` to the
 * `1 - cvr/2` cap, with Streak Boost either ON or OFF. Pinned by test.
 *
 * ⚠️ At the `hard` mark a green streak steps `0.20 → 0.40 → 0.70 → 0.90` — a
 * `+0.3` jump, the same magnitude the `aim`/`gim` ruling removed at the default
 * rate. Worth a look when the full control spec lands.
 *
 * The slider's *default* position is not settled here; `DEFAULT_TIER_CONFIG`
 * ships `0.10`, which is inside the range but is not one of the three marks.
 */
export const RATE_RANGE = Object.freeze({
  min: 0.03,
  max: 0.2,
  easy: 0.03,
  medium: 0.12,
  hard: 0.2,
});

/**
 * Is a Climb/Drop Rate within {@link RATE_RANGE}?
 *
 * Deliberately a predicate rather than a throwing validator: config arrives on
 * the Phaser path, where the whole module's posture is log-and-recover.
 */
export const isRateInRange = (rate: number): boolean =>
  Number.isFinite(rate) && rate >= RATE_RANGE.min && rate <= RATE_RANGE.max;

/**
 * The shipping v1 preset — Streak Boost **ON**. See {@link STREAK_BOOST_OFF}.
 *
 * ⚠️ `aim`/`gim` deliberately DIVERGE from the oracle sheet. Column I of
 * `ComplexityOracle.xlsx` carries `AIM = 1.0, GIM = 2.0`, and findings §1
 * ("Conflict 1") concluded the prose specs were stale for saying otherwise.
 * Product ruled the other way after the 2026-07-28 test session: a compounding
 * green step is `CAI*GIM + CAI*AIM`, so the sheet's pair makes the second one
 * `0.2 + 0.1 = 0.3` — roughly a third of the header progress bar's track in a
 * single phrase. The v1 pair is `AIM = 0.5, GIM = 1.0` — the value both
 * `complexity.md` and `complexity-engineering-spec.md` always specified — which
 * makes that same step `0.1 + 0.05 = 0.15`, and caps the largest step anywhere
 * in the climb at `0.25`.
 *
 * The recurrence itself is unchanged and still reproduces the sheet exactly;
 * see {@link ORACLE_SHEET_PRESET}, which the golden-vector suite runs against.
 * This is a parameter ruling, not a model change.
 */
export const DEFAULT_TIER_CONFIG: Readonly<TierConfig> = Object.freeze({
  complexityStart: 0.2,
  cvr: 0.2,
  anchor: 'center' as BandAnchor,
  cai: 0.1,
  cdi: 0.1,
  pad: 2,
  aim: 0.5,
  gim: 1.0,
  cat: 0.8,
  greenMax: 1.0,
  yellowMax: 3.0,
  restCadence: 4,
  restPhraseDepth: 0.25,
  restAccuracyThreshold: 95,
  restAccuracyFloor: 75,
  stuckWindow: 4,
});

/**
 * The oracle sheet's own preset — column I of `ComplexityOracle.xlsx`, which
 * every formula in the sheet references via `$I$70`–`$I$76`.
 *
 * This is NOT what ships; {@link DEFAULT_TIER_CONFIG} is. It exists so the
 * golden-vector suite can keep proving the recurrence reproduces the sheet row
 * for row, independently of which parameter pair product selects. Without the
 * separation, a parameter ruling would silently invalidate the only evidence
 * that the model is implemented correctly.
 */
export const ORACLE_SHEET_PRESET: Readonly<TierConfig> = Object.freeze({
  ...DEFAULT_TIER_CONFIG,
  cvr: 0.1,
  anchor: 'floor' as BandAnchor,
  aim: 1.0,
  gim: 2.0,
});

/**
 * The range the tracked position may occupy.
 *
 * Floor-anchored, the position is the bottom of the band, so it may sit anywhere
 * from 0 up to `1 - cvr` (the band's top stays inside `[0, 1]`).
 *
 * Centre-anchored, the position may climb all the way to the ceiling (1). The
 * band's lower half stays fixed at `cvr / 2`, but its upper half diminishes as
 * the position nears 1 — `bandFrom` clamps `crMax` at 1, so at position 1 the
 * band is `[1 - cvr/2, 1]` (the upper half has vanished). This lets the position
 * reach a true 100%: the band floor rises toward the ceiling, so the top is only
 * reachable by mastering the hardest in-tier content. The lower clamp is still
 * fenced `cvr / 2` off the bottom.
 *
 * These bounds are the recurrence's own clamps — `stepUp` saturates at `max`,
 * `stepDown` at `min` — not a post-hoc tidy-up, which is why they must follow
 * the anchor rather than being hardcoded.
 */
export function positionBounds(config: TierConfig): Readonly<{ min: number; max: number }> {
  const anchorMax = config.anchor === 'center' ? 1 : 1 - config.cvr;
  return config.anchor === 'center'
    ? { min: config.cvr / 2, max: config.positionCeiling ?? anchorMax }
    : { min: 0, max: config.positionCeiling ?? anchorMax };
}

/**
 * The band a position denotes, under this config's {@link TierConfig.anchor}.
 *
 * Clamped to `[0, 1]` for safety, though {@link positionBounds} should already
 * keep a well-formed trajectory clear of both ends.
 */
export function bandFrom(position: number, config: TierConfig): Readonly<Band> {
  const half = config.anchor === 'center' ? config.cvr / 2 : 0;
  return Object.freeze({
    position,
    crMin: Math.max(position - half, 0),
    crMax: Math.min(position - half + config.cvr, 1),
  });
}

/**
 * A position and the band it denotes. Always construct via {@link bandFrom} so
 * the three stay consistent with the tier's anchoring.
 */
export interface Band {
  /**
   * The scalar the recurrence tracks — the user's position in the tier's
   * complexity spectrum, constrained to {@link positionBounds}.
   *
   * This, not `crMin`, is the quantity the model steps and the one every
   * level-comparison should use (the CAT gate, stuck-detection, telemetry).
   * Under the sheet's floor anchoring the two coincide, which is why earlier
   * revisions had no separate field; under centre anchoring they differ by
   * `cvr / 2` and conflating them silently shifts every threshold.
   */
  position: number;
  /** Band floor in effect. */
  crMin: number;
  /** Band ceiling. */
  crMax: number;
}

/**
 * Streak Boost OFF — oracle column L.
 *
 * Setting `aim = 0, gim = 1` makes every green step exactly `cai` and every red
 * step exactly `cdi`, which is precisely the "flat Climb/Drop Rate" behaviour
 * `complexity.md` describes for the toggle. Streak Boost therefore needs no
 * special-casing in the recurrence — it is a two-parameter swap.
 *
 * With the v1 ON pair (`aim = 0.5, gim = 1.0`) the toggle now differs only in
 * the arithmetic term, so ON compounds more gently than it did under the
 * sheet's pair while still climbing strictly faster than OFF.
 */
export const STREAK_BOOST_OFF: Readonly<Pick<TierConfig, 'aim' | 'gim'>> = Object.freeze({
  aim: 0.0,
  gim: 1.0,
});

/**
 * One row of the model's output: the band in effect for a single assessment
 * phrase.
 *
 * `idx` is the 1-based index into the *assessment stream*, NOT the render
 * queue. Rest phrases are interleaved into playback but are not members of the
 * assessment stream and do not consume an index.
 */
export interface AssessmentBand extends Band {
  /** 1-based assessment-stream index. */
  idx: number;
  /** The PAD-delayed verdict that drove this row: `verdict[idx - pad]`.
   *  `undefined` for the seed rows, which have no driver. */
  driver?: Verdict;
  /** The oracle's `PLATEAU` value for this row, retained under a truthful name.
   *
   *  ⚠️ Despite the sheet's name this is NOT stuck-detection — it is an
   *  anti-jitter cooldown: `>= 5 - pad` means the band moved on the previous
   *  step, so this step is suppressed. For genuine stuck-detection see
   *  `isStuck()` in `flowRegeneration.ts`. Kept for telemetry and for
   *  reproducing any row against the oracle by hand. */
  cooldown: number;
}
